import arabicUiCopy from "../../statics/arabicUiCopy";
import localization from "../../statics/localization";

export const rentalCopy = () =>
  localization.getLanguage() === "arb" ? arabicUiCopy.rental : localization.getLanguage() === "tr"
    ? {
        choose: "Kiralama seçeneği",
        confirmTitle: "Kiralamayı onayla",
        rentalFee: "Kiralama bedeli",
        viewingPeriod: "İzleme süresi",
        pinNext: "Devam etmek için bir sonraki adımda profil PIN’inizi girin.",
        days: "gün",
        packages: "Paketlerim",
        unavailable:
          "Paketiniz Bu İçeriği Kapsamamaktadır",
        purchaseUnavailable:
          "Bu içerik için kalıcı satın alma şu anda kullanılamıyor.",
        changed:
          "Kiralama teklifi değişti veya süresi doldu. Güncel seçenekleri inceleyip yeniden onaylayın.",
        missing:
          "Bu kiralama seçeneği artık kullanılamıyor. Güncel seçenekleri kontrol edin.",
        refresh: "Seçenekleri yenile",
        history: "İşlem geçmişim",
        inProgress:
          "Bu içerik için bir kiralama işlemi sürüyor. Tekrar ödeme yapmadan önce işlem geçmişinizi kontrol edin.",
        failed: "Kiralama tamamlanamadı.",
        billed: "Kiralama bedeli faturanıza yansıtılmıştır.",
        demoBilled: "Demo işlem: Kiralama bedeli faturanıza yansıtılmıştır. Gerçek tahsilat yapılmamıştır.",
        completed: "Kiralama işlemi tamamlandı.",
        accessReady: "İzleme hakkınız hazır. Ek ücret alınmadı.",
        contextChanged:
          "Oturum veya profil değişti. Yeniden kiralamadan önce işlemi başlattığınız hesabın işlem geçmişini kontrol edin.",
        invalidOffer: "Kiralama bilgileri eksik. Güncel seçenekleri yükleyin.",
        end: "Bu içeriğin yayın hakkı şu tarihte sona eriyor:",
        consent: "Kiralama süresinin bu tarihte biteceğini kabul ediyorum.",
        emptyPackages: "Aktif paketiniz bulunmuyor.",
        packageNote:
          "Hesabınıza tanımlı paketleri ve geçerlilik tarihlerini görüntüleyin.",
        active: "Aktif",
        inactive: "Aktif değil",
        suspended: "Askıya alınmış",
        expired: "Süresi dolmuş",
        canceled: "İptal edilmiş",
        start: "Başlangıç",
        endDate: "Bitiş"
      }
    : {
        choose: "Rental option",
        confirmTitle: "Confirm your rental",
        rentalFee: "Rental price",
        viewingPeriod: "Viewing period",
        pinNext: "Enter your profile PIN in the next step to continue.",
        days: "days",
        packages: "My packages",
        unavailable:
          "Your package does not include this content.",
        purchaseUnavailable:
          "Permanent purchase is currently unavailable for this title.",
        changed:
          "This rental offer changed or expired. Review the latest options and confirm again.",
        missing:
          "This rental option is no longer available. Check the latest options.",
        refresh: "Refresh options",
        history: "Transaction history",
        inProgress:
          "A rental is already in progress for this title. Check your transaction history before paying again.",
        failed: "The rental could not be completed.",
        billed: "The rental fee has been added to your bill.",
        demoBilled: "Demo: The rental fee has been added to your bill. No actual charge was made.",
        completed: "The rental is complete.",
        accessReady: "Your viewing access is ready. No additional charge was made.",
        contextChanged:
          "Your session or profile changed. Check the transaction history of the account where you started before renting again.",
        invalidOffer: "Rental details are incomplete. Load the latest options.",
        end: "The publication rights for this title end at:",
        consent: "I accept that this rental will end at that time.",
        emptyPackages: "You have no active packages.",
        packageNote: "View your account’s packages and validity dates.",
        active: "Active",
        inactive: "Inactive",
        suspended: "Suspended",
        expired: "Expired",
        canceled: "Canceled",
        start: "Start",
        endDate: "End"
      };

// Prefer the numeric DTO field. Older MW responses use either "32.99 TL" or "32,99".
export function mapRentalOffer(offer) {
  const match = String(offer.Price ?? "")
    .trim()
    .match(/^(\d+(?:[.,]\d+)?)\s*(TRY|TL|₺|[A-Z]{3})?$/i);
  const amount =
    typeof offer.PriceValue === "number"
      ? offer.PriceValue
      : match
      ? Number(match[1].replace(",", "."))
      : NaN;
  const currency = String(offer.Currency || match?.[2] || "TRY").toUpperCase();
  return {
    ...offer,
    id: offer.UId,
    type: "RENTAL",
    price: amount,
    currency: currency === "TL" || currency === "₺" ? "TRY" : currency,
    duration: offer.RentPeriod
  };
}

export const isRentalOffer = (offer) =>
  !!offer?.id &&
  offer.type === "RENTAL" &&
  Number.isFinite(offer.price) &&
  offer.price > 0 &&
  Number.isInteger(offer.duration) &&
  offer.duration > 0;

export const isPurchaseOffer = offer => !!offer?.id && offer.type === "PURCHASE" &&
  Number.isFinite(offer.price) && offer.price > 0;
export const isPayableOffer = offer => isRentalOffer(offer) || isPurchaseOffer(offer);

export function offerPrice(offer) {
  if (!Number.isFinite(offer?.price)) return "—";
  const amount = offer.price.toLocaleString(
    localization.getLanguage() === "arb" ? "ar" : localization.getLanguage() === "tr" ? "tr-TR" : "en-GB",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
  );
  return `${amount} ${offer.currency === "TRY" ? "₺" : offer.currency || "₺"}`;
}

export function publicationEnd(offer) {
  const date = new Date(offer?.AvailabilityEndDate);
  if (!offer?.AvailabilityEndDate || !Number.isFinite(date.getTime()))
    return null;
  return date.toLocaleString(
    localization.getLanguage() === "arb" ? "ar" : localization.getLanguage() === "tr" ? "tr-TR" : "en-GB",
    { timeZone: "Europe/Istanbul", timeZoneName: "short" }
  );
}

// RentalHistoryService emits Istanbul wall time, not ISO dates. Normalize before filtering.
export function rentalHistoryDate(value) {
  const parts =
    typeof value === "string" &&
    value.match(/^(\d{2})\.(\d{2})\.(\d{4}) (\d{2}):(\d{2})$/);
  return parts
    ? `${parts[3]}-${parts[2]}-${parts[1]}T${parts[4]}:${parts[5]}:00+03:00`
    : value;
}
